import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';

import apiOffers from 'api/offers';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import UserSelect from 'components/layout/forms/UserSelect';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

const emptyForm = {
  attachmentsNote: '',
  cargo: '',
  deliveryTermsTemplateId: '',
  deliveryTimeNote: '',
  invoiceNote: '',
  offerTextTemplateId: '',
  paymentTerm: '',
  priceListTemplateId: '',
  signatureUser: { label: '', value: '' },
};

interface IChoice {
  id: number;
  name: string;
}

const DELIVERY_TERMS = 'delivery-terms';

const idOrNull = (id: string): null | number =>
  id === '' ? null : parseInt(id, 10);

/**
 * The basic information the new offers and preliminary offers of a department get: the
 * payment term, cargo, signer, invoicing, delivery time and attachments. The offer can
 * change them; the offers made earlier keep their own.
 */
const OfferDefaults = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);

  // The departments whose offers the user may write
  const [departments, setDepartments] = useState<
    { id: number; name: string }[]
  >([]);
  const [departmentId, setDepartmentId] = useState<number>();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // The texts of the archive and the price lists of the registry to choose from
  const [offerTexts, setOfferTexts] = useState<IChoice[]>([]);
  const [deliveryTerms, setDeliveryTerms] = useState<IChoice[]>([]);
  const [priceLists, setPriceLists] = useState<IChoice[]>([]);

  useEffect(() => {
    const toChoices = (list: { id: number; name: string }[]) =>
      list.map((item) => ({ id: item.id, name: item.name }));

    apiOffers.textTemplates
      .get()
      .then((texts) => setOfferTexts(toChoices(texts)))
      .catch(() => setOfferTexts([]));
    apiOffers.textTemplates
      .get(undefined, DELIVERY_TERMS)
      .then((texts) => setDeliveryTerms(toChoices(texts)))
      .catch(() => setDeliveryTerms([]));
    apiOffers.priceListTemplates
      .get()
      .then((lists) =>
        setPriceLists(
          lists.map((list) => ({
            id: list.id,
            name: `${list.name} (${list.rowCount})`,
          }))
        )
      )
      .catch(() => setPriceLists([]));
  }, []);

  useEffect(() => {
    // The user's own department first, where the user's new offers are made
    Promise.all([apiOffers.departments.get(true), apiOffers.defaults.get()])
      .then(([writable, own]) => {
        setDepartments(writable);
        setDepartmentId(
          writable.find(
            (department) => department.id === own.organizationUnitId
          )?.id ?? writable[0]?.id
        );
      })
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    if (departmentId === undefined) {
      return;
    }

    apiOffers.defaults
      .get(departmentId)
      .then((defaults) =>
        setForm({
          attachmentsNote: defaults.attachmentsNote ?? '',
          cargo: defaults.cargo ?? '',
          deliveryTermsTemplateId: String(
            defaults.deliveryTermsTemplateId ?? ''
          ),
          deliveryTimeNote: defaults.deliveryTimeNote ?? '',
          invoiceNote: defaults.invoiceNote ?? '',
          offerTextTemplateId: String(defaults.offerTextTemplateId ?? ''),
          priceListTemplateId: String(defaults.priceListTemplateId ?? ''),
          paymentTerm:
            defaults.paymentTerm === null ? '' : String(defaults.paymentTerm),
          signatureUser:
            defaults.signatureUser === null
              ? { label: '', value: '' }
              : {
                  label: `${defaults.signatureUser.firstName} ${defaults.signatureUser.lastName}`,
                  value: defaults.signatureUser.id,
                },
        })
      )
      .catch(() => setForm(emptyForm));
  }, [departmentId]);

  const setField = (name: string) => (event: any) =>
    setForm({ ...form, [name]: event.target.value });

  const paymentTermValid =
    form.paymentTerm === '' || /^\d+$/.test(form.paymentTerm.trim());

  const save = async () => {
    if (departmentId === undefined || saving || !paymentTermValid) {
      return;
    }

    setSaving(true);
    try {
      await apiOffers.defaults.put(departmentId, {
        attachmentsNote: form.attachmentsNote,
        cargo: form.cargo,
        deliveryTermsTemplateId: idOrNull(form.deliveryTermsTemplateId),
        deliveryTimeNote: form.deliveryTimeNote,
        invoiceNote: form.invoiceNote,
        offerTextTemplateId: idOrNull(form.offerTextTemplateId),
        priceListTemplateId: idOrNull(form.priceListTemplateId),
        paymentTerm:
          form.paymentTerm.trim() === ''
            ? null
            : parseInt(form.paymentTerm.trim(), 10),
        signatureUserId:
          form.signatureUser.value === '' ? null : form.signatureUser.value,
      });
      dispatch(addMessage(t(`${translations.offers}:defaults.Saved`)));
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:defaults.Save failed`)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <h1>{t(`${translations.offers}:defaults.Title`)}</h1>
      <p>{t(`${translations.offers}:defaults.Info`)}</p>
      {departments.length === 0 ? (
        <p>{t(`${translations.offers}:defaults.No departments`)}</p>
      ) : (
        <>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="offer-defaults-department">
                  {t(`${translations.offers}:defaults.Department`)}
                </Label>
                <Input
                  id="offer-defaults-department"
                  onChange={(event) =>
                    setDepartmentId(parseInt(event.target.value, 10))
                  }
                  type="select"
                  value={departmentId ?? ''}
                >
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="offer-defaults-payment-term">
                  {t(`${translations.offers}:Payment Term`)}
                </Label>
                <Input
                  id="offer-defaults-payment-term"
                  invalid={!paymentTermValid}
                  onChange={setField('paymentTerm')}
                  value={form.paymentTerm}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <UserSelect
                label={t(`${translations.offers}:Signature`)}
                onChange={(user: { label: string; value: string } | null) =>
                  setForm({
                    ...form,
                    signatureUser: user ?? { label: '', value: '' },
                  })
                }
                value={form.signatureUser}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="offer-defaults-cargo">
                  {t(`${translations.offers}:Cargo`)}
                </Label>
                <Input
                  id="offer-defaults-cargo"
                  onChange={setField('cargo')}
                  value={form.cargo}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="offer-defaults-invoicing">
                  {t(`${translations.offers}:Invoicing`)}
                </Label>
                <Input
                  id="offer-defaults-invoicing"
                  onChange={setField('invoiceNote')}
                  value={form.invoiceNote}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="offer-defaults-delivery-time">
                  {t(`${translations.offers}:Delivery time`)}
                </Label>
                <Input
                  id="offer-defaults-delivery-time"
                  onChange={setField('deliveryTimeNote')}
                  value={form.deliveryTimeNote}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="offer-defaults-attachments">
                  {t(`${translations.offers}:Attachments`)}
                </Label>
                <Input
                  id="offer-defaults-attachments"
                  onChange={setField('attachmentsNote')}
                  value={form.attachmentsNote}
                />
              </FormGroup>
            </Col>
          </Row>
          {/* From the text archive and the price list registry */}
          <Row>
            {[
              {
                choices: offerTexts,
                field: 'offerTextTemplateId',
                label: 'defaults.Offer text',
                value: form.offerTextTemplateId,
              },
              {
                choices: deliveryTerms,
                field: 'deliveryTermsTemplateId',
                label: 'defaults.Delivery terms',
                value: form.deliveryTermsTemplateId,
              },
              {
                choices: priceLists,
                field: 'priceListTemplateId',
                label: 'defaults.Price list',
                value: form.priceListTemplateId,
              },
            ].map((choice) => (
              <Col key={choice.field} md={4}>
                <FormGroup>
                  <Label for={`offer-defaults-${choice.field}`}>
                    {t(`${translations.offers}:${choice.label}`)}
                  </Label>
                  <Input
                    id={`offer-defaults-${choice.field}`}
                    onChange={setField(choice.field)}
                    type="select"
                    value={choice.value}
                  >
                    <option value="">
                      {t(`${translations.offers}:defaults.None`)}
                    </option>
                    {choice.choices.map((item) => (
                      <option key={item.id} value={String(item.id)}>
                        {item.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            ))}
          </Row>
          <PrimaryButton disabled={saving || !paymentTermValid} onClick={save}>
            {t(`${translations.common}:Save`).toLocaleUpperCase()}
          </PrimaryButton>
        </>
      )}
    </PageContainer>
  );
};

export default OfferDefaults;
